import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Scale, Plus, X, Loader2, Trash2, Check, Copy, Save, Pencil, Search, Printer } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatDate, todayAsuncion } from "../../utils/format";
import { toast } from "sonner";
// Fix CX (2026-09-14): "se podría habilitar la opción de imprimir PDF también en la
// comparativa auxiliar" (Sergio, 14/09). Es el mismo generador del Análisis de
// Presupuestos —la comparativa auxiliar tiene ese mismo formato—, con el encabezado
// y los totales de una comparativa.
import { generateAPPDF } from "./helpers";

const imprimir = (c) => {
  if (!(c.provider_columns || []).length) { toast.error("Agregá al menos un proveedor antes de imprimir"); return; }
  const conDatos = (c.analysis_items || [])
    .map((it, i) => [it, i])
    .filter(([it]) => (it.item_name || "").trim() || (it.prices || []).some(p => Number(p?.unit_price) > 0))
    .map(([, i]) => i);
  if (!conDatos.length) { toast.error("Agregá al menos un ítem antes de imprimir"); return; }
  try {
    generateAPPDF(c, { comparativaAuxiliar: true, visibleItemIdxs: conDatos });
  } catch (err) {
    toast.error("No se pudo generar el PDF");
    console.error(err);
  }
};

// Fix CI (2026-09-11): "Comparativa Auxiliar. Donde debe estar el formato de Análisis de
// presupuestos con el objetivo de tener borradores de comparativas rápidas fuera del
// proceso de compra normal" (Sergio, 11/09). No genera pedidos, órdenes ni pagos.
const BASE = `${API}/purchases/comparativas-auxiliares`;
const opts = { withCredentials: true };
const precioVacio = () => ({ unit_price: 0, obs: "" });
const itemVacio = (n) => ({ item_name: "", unit: "un", quantity: 1, prices: Array.from({ length: n }, precioVacio), selected_idx: -1, allocations: [] });
// Fix EI (2026-09-27): "En comparativa auxiliar que las primeras 3 columnas queden congeladas
// por favor. Y que se puedan asignar cantidades a cada columna. Bien parecido a como tenemos
// en Análisis de presupuestos" (CEN). Mismo modelo del AP: allocations [{provider_idx,
// quantity}]; una comparativa vieja (solo "elegido") se abre con la cantidad entera ahí.
const allocsDe = (it) => (Array.isArray(it.allocations) ? it.allocations
  : (typeof it.selected_idx === "number" && it.selected_idx >= 0 ? [{ provider_idx: it.selected_idx, quantity: Number(it.quantity) || 0 }] : []));
const conAllocs = (c) => ({ ...c, analysis_items: (c.analysis_items || []).map(it => ({ ...it, allocations: allocsDe(it) })) });
const CMP_STICKY_CSS = `
.cmp-tbl { border-collapse: separate; border-spacing: 0; }
.cmp-tbl .cmp-c1, .cmp-tbl .cmp-c2, .cmp-tbl .cmp-c3 { position: sticky; z-index: 20; }
.cmp-tbl .cmp-c1 { left: 0; width: 230px; min-width: 230px; }
.cmp-tbl .cmp-c2 { left: 230px; width: 64px; min-width: 64px; }
.cmp-tbl .cmp-c3 { left: 294px; width: 80px; min-width: 80px; box-shadow: 6px 0 8px -6px rgba(15,23,42,.45); }
.cmp-tbl thead .cmp-c1, .cmp-tbl thead .cmp-c2, .cmp-tbl thead .cmp-c3 { z-index: 30; }
`;
const subtotal = (it, ci) => (Number(it.prices?.[ci]?.unit_price) || 0) * (Number(it.quantity) || 0);
const cuerpo = (c) => ({ titulo: c.titulo || "", notas: c.notas || "", provider_columns: c.provider_columns || [], analysis_items: c.analysis_items || [] });

function Editor({ inicial, onClose, onSaved }) {
  const [c, setC] = useState(() => conAllocs(JSON.parse(JSON.stringify(inicial))));
  const [nuevoProv, setNuevoProv] = useState("");
  const [saving, setSaving] = useState(false);
  const [sucio, setSucio] = useState(false);
  const cols = c.provider_columns || [];
  const items = c.analysis_items || [];
  const set = (patch) => { setC(prev => ({ ...prev, ...patch })); setSucio(true); };
  const setItem = (ii, patch) => set({ analysis_items: items.map((it, i) => (i === ii ? { ...it, ...patch } : it)) });
  const setPrecio = (ii, ci, campo, v) => set({
    analysis_items: items.map((it, i) => {
      if (i !== ii) return it;
      const prices = cols.map((_, k) => ({ ...precioVacio(), ...(it.prices?.[k] || {}) }));
      prices[ci] = { ...prices[ci], [campo]: campo === "unit_price" ? (Number(v) || 0) : v };
      return { ...it, prices };
    }),
  });
  const agregarProv = () => {
    const n = nuevoProv.trim();
    if (!n) { toast.error("Escribí el nombre del proveedor"); return; }
    if (cols.some(x => x.trim().toLowerCase() === n.toLowerCase())) { toast.error("Ese proveedor ya está en la comparativa"); return; }
    set({
      provider_columns: [...cols, n],
      analysis_items: items.map(it => ({ ...it, prices: [...cols.map((_, k) => it.prices?.[k] || precioVacio()), precioVacio()] })),
    });
    setNuevoProv("");
  };
  const quitarProv = (ci) => {
    if (!window.confirm(`¿Quitar a ${cols[ci]} de la comparativa?`)) return;
    set({
      provider_columns: cols.filter((_, k) => k !== ci),
      analysis_items: items.map(it => ({
        ...it,
        prices: (it.prices || []).filter((_, k) => k !== ci),
        selected_idx: it.selected_idx === ci ? -1 : (it.selected_idx > ci ? it.selected_idx - 1 : it.selected_idx),
        allocations: allocsDe(it).filter(a => a.provider_idx !== ci)
          .map(a => ({ ...a, provider_idx: a.provider_idx > ci ? a.provider_idx - 1 : a.provider_idx })),
      })),
    });
  };
  const mejorPrecio = () => set({
    analysis_items: items.map(it => {
      let best = -1;
      cols.forEach((_, ci) => {
        const p = Number(it.prices?.[ci]?.unit_price) || 0;
        if (p > 0 && (best < 0 || p < Number(it.prices[best].unit_price))) best = ci;
      });
      return { ...it, selected_idx: best, allocations: best >= 0 ? [{ provider_idx: best, quantity: Number(it.quantity) || 0 }] : [] };
    }),
  });
  // Fix EI: elegir / quitar un proveedor (con lo que falta asignar) y la cantidad de cada uno.
  const alternarProv = (ii, ci) => {
    const it = items[ii];
    const al = allocsDe(it);
    if (al.some(a => a.provider_idx === ci)) { setItem(ii, { allocations: al.filter(a => a.provider_idx !== ci) }); return; }
    const asignado = al.reduce((s, a) => s + (Number(a.quantity) || 0), 0);
    setItem(ii, { allocations: [...al, { provider_idx: ci, quantity: Math.max((Number(it.quantity) || 0) - asignado, 0) }] });
  };
  const setCant = (ii, ci, v) => setItem(ii, {
    allocations: allocsDe(items[ii]).map(a => (a.provider_idx === ci ? { ...a, quantity: Number(v) || 0 } : a)),
  });
  const totales = cols.map((_, ci) => items.reduce((s, it) => s + subtotal(it, ci), 0));
  const conPrecio = totales.filter(t => t > 0);
  const minTotal = conPrecio.length ? Math.min(...conPrecio) : null;
  const totalSel = items.reduce((s, it) => s + allocsDe(it).reduce((t, a) => t + (Number(it.prices?.[a.provider_idx]?.unit_price) || 0) * (Number(a.quantity) || 0), 0), 0);

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${BASE}/${c._id}`, cuerpo(c), opts);
      setC(data);
      setSucio(false);
      toast.success("Comparativa guardada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar la comparativa"); }
    finally { setSaving(false); }
  };
  const cerrar = () => {
    if (sucio && !window.confirm("Hay cambios sin guardar. ¿Cerrar igual?")) return;
    onClose();
  };
  const inp = "w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none bg-white";

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="comparativa-editor">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={cerrar} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <input value={c.titulo || ""} onChange={e => set({ titulo: e.target.value })} placeholder="Título de la comparativa"
              className="w-full text-base font-bold text-gray-900 border-0 border-b border-dashed border-gray-200 focus:border-[#2E6DA4] focus:outline-none px-0 py-1" data-testid="comparativa-titulo" />
            <p className="text-[11px] text-gray-400 mt-1">Borrador fuera del proceso de compra · no genera pedidos ni órdenes{sucio ? " · cambios sin guardar" : ""}</p>
          </div>
          {/* Fix CX (2026-09-14): imprime lo que está en pantalla, incluso sin guardar. */}
          <button onClick={() => imprimir(c)} title="Imprimir PDF" className="flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid="comparativa-pdf">
            <Printer size={13} /> PDF
          </button>
          <button onClick={cerrar} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              {totalSel > 0 && <p className="text-sm font-bold text-emerald-600" data-testid="comparativa-total-sel">Total seleccionado: {formatPYG(totalSel)}</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input value={nuevoProv} onChange={e => setNuevoProv(e.target.value)} onKeyDown={e => e.key === "Enter" && agregarProv()}
                placeholder="Nombre del proveedor..." className="border border-violet-300 rounded-xl px-3 py-1.5 text-xs w-48 focus:outline-none focus:ring-2 focus:ring-violet-200" data-testid="comparativa-nuevo-prov" />
              <button onClick={agregarProv} className="flex items-center gap-1.5 border border-dashed border-violet-400 text-violet-600 hover:bg-violet-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid="comparativa-agregar-prov">
                <Plus size={13} /> Agregar Proveedor
              </button>
              {cols.length > 1 && (
                <button onClick={mejorPrecio} className="flex items-center gap-1.5 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-xl text-xs font-semibold" data-testid="comparativa-mejor-precio">
                  <Check size={13} /> Elegir el mejor precio por ítem
                </button>
              )}
            </div>
          </div>

          <style>{CMP_STICKY_CSS}</style>
          <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
            <table className="text-xs cmp-tbl" style={{ minWidth: "100%" }}>
              <thead>
                <tr className="bg-[#1E3A5F] text-white">
                  <th className="cmp-c1 bg-[#1E3A5F] text-left px-3 py-2.5 font-semibold">Ítem</th>
                  <th className="cmp-c2 bg-[#1E3A5F] px-2 py-2.5 font-semibold text-center">UM</th>
                  <th className="cmp-c3 bg-[#1E3A5F] px-2 py-2.5 font-semibold text-center">Cant.</th>
                  {cols.map((col, ci) => (
                    <th key={ci} colSpan={5} className="px-2 py-2 font-semibold text-center border-l border-white/20 min-w-[360px]">
                      <div className="flex items-center gap-1.5">
                        <input value={col} onChange={e => set({ provider_columns: cols.map((x, k) => (k === ci ? e.target.value : x)) })}
                          className="flex-1 bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-xs text-white text-center focus:outline-none" data-testid={`comparativa-prov-${ci}`} />
                        <button onClick={() => quitarProv(ci)} title="Quitar proveedor" className="p-1 rounded hover:bg-white/10" data-testid={`comparativa-quitar-prov-${ci}`}><X size={12} /></button>
                      </div>
                    </th>
                  ))}
                  <th className="w-8"></th>
                </tr>
                {cols.length > 0 && (
                  <tr className="bg-[#2E6DA4]/80 text-blue-100 text-[10px]">
                    <th className="cmp-c1 bg-[#5282b3]"></th><th className="cmp-c2 bg-[#5282b3]"></th><th className="cmp-c3 bg-[#5282b3]"></th>
                    {cols.map((_, ci) => (
                      <th key={ci} colSpan={5} className="border-l border-white/10">
                        <div className="grid grid-cols-5 text-center">
                          <span className="py-1 px-2 border-r border-white/10">P.U.</span>
                          <span className="py-1 px-2 border-r border-white/10">Cant.</span>
                          <span className="py-1 px-2 border-r border-white/10">Subtotal</span>
                          <span className="py-1 px-2 border-r border-white/10">Obs.</span>
                          <span className="py-1 px-1">Sel.</span>
                        </div>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it, ii) => {
                  const fondo = ii % 2 === 0 ? "bg-white" : "bg-gray-50";
                  return (
                  <tr key={ii} className={fondo} data-testid={`comparativa-item-${ii}`}>
                    <td className={`cmp-c1 ${fondo} px-2 py-1.5`}><input value={it.item_name} onChange={e => setItem(ii, { item_name: e.target.value })} placeholder="Descripción" className={inp} data-testid={`comparativa-item-nombre-${ii}`} /></td>
                    <td className={`cmp-c2 ${fondo} px-1 py-1.5`}><input value={it.unit} onChange={e => setItem(ii, { unit: e.target.value })} className={`${inp} text-center`} data-testid={`comparativa-item-um-${ii}`} /></td>
                    <td className={`cmp-c3 ${fondo} px-1 py-1.5`}><input type="number" min="0" value={it.quantity} onChange={e => setItem(ii, { quantity: Number(e.target.value) || 0 })} className={`${inp} text-center`} data-testid={`comparativa-item-cant-${ii}`} /></td>
                    {cols.map((_, ci) => {
                      const p = it.prices?.[ci] || {};
                      const alloc = allocsDe(it).find(a => a.provider_idx === ci);
                      const gana = !!alloc;
                      const cant = gana ? (Number(alloc.quantity) || 0) : 0;
                      const sub = (Number(p.unit_price) || 0) * cant;
                      return (
                        <td key={ci} colSpan={5} className={`border-l border-gray-100 p-0 ${gana ? "bg-emerald-50" : ""}`}>
                          <div className="grid grid-cols-5">
                            <div className="px-1.5 py-1.5">
                              <input type="number" min="0" value={p.unit_price || ""} placeholder="0" onChange={e => setPrecio(ii, ci, "unit_price", e.target.value)}
                                className={`${inp} text-right ${gana ? "border-emerald-300 font-semibold" : ""}`} data-testid={`comparativa-precio-${ii}-${ci}`} />
                            </div>
                            <div className="px-1.5 py-1.5">
                              <input type="number" min="0" value={gana ? cant : ""} placeholder="—" disabled={!gana} onChange={e => setCant(ii, ci, e.target.value)}
                                className={`${inp} text-right ${gana ? "border-emerald-300 font-semibold" : "bg-gray-50 text-gray-300"}`} data-testid={`comparativa-cant-${ii}-${ci}`} />
                            </div>
                            <div className={`px-2 py-2 text-right text-xs flex items-center justify-end border-r border-gray-100 ${gana ? "text-emerald-700 font-bold" : "text-gray-600"}`}>{sub > 0 ? formatPYG(sub) : "—"}</div>
                            <div className="px-1.5 py-1.5"><input value={p.obs || ""} placeholder="Obs." onChange={e => setPrecio(ii, ci, "obs", e.target.value)} className={inp} /></div>
                            <div className="px-1 py-1.5 flex items-center justify-center">
                              <button onClick={() => alternarProv(ii, ci)} title={`Elegir ${cols[ci]}`}
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${gana ? "bg-emerald-500 border-emerald-500" : "bg-white border-gray-300 hover:border-emerald-400"}`}
                                data-testid={`comparativa-sel-${ii}-${ci}`}>
                                {gana && <Check size={11} className="text-white" />}
                              </button>
                            </div>
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-1 text-center"><button onClick={() => set({ analysis_items: items.filter((_, i) => i !== ii) })} title="Quitar ítem" className="p-1 text-red-400 hover:text-red-600" data-testid={`comparativa-quitar-item-${ii}`}><X size={13} /></button></td>
                  </tr>
                  );
                })}
              </tbody>
              {cols.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-100 font-bold text-xs border-t-2 border-gray-300">
                    <td colSpan={3} className="cmp-c1 bg-gray-100 px-3 py-2.5 text-gray-600">TOTAL POR PROVEEDOR</td>
                    {totales.map((t, ci) => (
                      <td key={ci} colSpan={5} className={`px-3 py-2.5 text-center border-l border-gray-200 ${minTotal !== null && t === minTotal ? "text-emerald-700 bg-emerald-50" : "text-[#2E6DA4]"}`} data-testid={`comparativa-total-${ci}`}>
                        {formatPYG(t)}{minTotal !== null && t === minTotal && conPrecio.length > 1 ? " · más bajo" : ""}
                      </td>
                    ))}
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          <button onClick={() => set({ analysis_items: [...items, itemVacio(cols.length)] })} className="flex items-center gap-1.5 text-xs text-[#2E6DA4] font-semibold border border-dashed border-[#2E6DA4]/40 px-3 py-2 rounded-lg hover:bg-blue-50" data-testid="comparativa-agregar-item">
            <Plus size={12} /> Agregar ítem
          </button>
          <textarea value={c.notas || ""} onChange={e => set({ notas: e.target.value })} rows={2} placeholder="Notas (condiciones, plazos, contactos...)"
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm resize-none" data-testid="comparativa-notas" />
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={cerrar} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cerrar</button>
          <button onClick={guardar} disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="comparativa-guardar">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ComparativaAuxiliar() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [abierta, setAbierta] = useState(null);
  const [creando, setCreando] = useState(false);
  const [q, setQ] = useState("");

  const cargar = useCallback(async () => {
    try {
      const { data } = await axios.get(BASE, opts);
      setLista(Array.isArray(data) ? data : []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar las comparativas"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async (body, abrir) => {
    setCreando(true);
    try {
      const { data } = await axios.post(BASE, body, opts);
      if (abrir) setAbierta(data); else toast.success(`Copia creada: ${data.titulo}`);
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al crear la comparativa"); }
    finally { setCreando(false); }
  };
  const borrar = async (c) => {
    if (!window.confirm(`¿Borrar la comparativa "${c.titulo}"?`)) return;
    try { await axios.delete(`${BASE}/${c._id}`, opts); toast.success("Comparativa borrada"); cargar(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al borrar"); }
  };
  const visibles = lista.filter(c => !q.trim() || `${c.titulo} ${(c.provider_columns || []).join(" ")} ${(c.analysis_items || []).map(i => i.item_name).join(" ")}`.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="comparativa-auxiliar">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-2"><Scale size={26} className="text-[#2E6DA4]" /> Comparativa Auxiliar</h1>
          <p className="text-sm text-gray-500 mt-0.5">Borradores de comparativas rápidas entre proveedores, con el formato del Análisis de presupuestos · fuera del proceso de compra</p>
        </div>
        <button onClick={() => crear({ titulo: `Comparativa ${formatDate(todayAsuncion())}` }, true)} disabled={creando}
          className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="comparativa-nueva">
          {creando ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Nueva comparativa
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por título, proveedor o ítem..." className="w-full border border-gray-200 rounded-xl pl-9 pr-3 py-2 text-sm" data-testid="comparativa-buscar" />
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50/50 text-xs text-gray-400 uppercase">
            <th className="text-left px-4 py-3">Comparativa</th><th className="text-right px-4 py-3">Ítems</th><th className="text-right px-4 py-3">Proveedores</th>
            <th className="text-right px-4 py-3">Total seleccionado</th><th className="text-left px-4 py-3">Actualizada</th><th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="py-10 text-center"><Loader2 className="animate-spin mx-auto text-gray-400" size={18} /></td></tr>
              : visibles.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-gray-400">{lista.length ? "Sin coincidencias" : "Todavía no hay comparativas. Creá la primera con \"Nueva comparativa\"."}</td></tr>
              : visibles.map(c => (
                <tr key={c._id} className="border-t border-gray-50 hover:bg-gray-50/60" data-testid={`comparativa-row-${c._id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => setAbierta(c)} className="font-semibold text-gray-900 hover:text-[#2E6DA4] text-left">{c.titulo}</button>
                    <p className="text-[11px] text-gray-400">{(c.provider_columns || []).join(" · ") || "Sin proveedores"}</p>
                  </td>
                  <td className="px-4 py-3 text-right">{(c.analysis_items || []).length}</td>
                  <td className="px-4 py-3 text-right">{(c.provider_columns || []).length}</td>
                  <td className="px-4 py-3 text-right font-semibold">{c.total_seleccionado ? formatPYG(c.total_seleccionado) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{formatDate(c.updated_at)} · {c.updated_by_name || c.created_by_name}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setAbierta(c)} title="Abrir" className="p-1.5 rounded-lg text-[#2E6DA4] hover:bg-blue-50" data-testid={`comparativa-abrir-${c._id}`}><Pencil size={14} /></button>
                    {/* Fix CX (2026-09-14): imprimir sin tener que abrirla. */}
                    <button onClick={() => imprimir(c)} title="Imprimir PDF" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" data-testid={`comparativa-pdf-${c._id}`}><Printer size={14} /></button>
                    <button onClick={() => crear({ ...cuerpo(c), titulo: `${c.titulo} (copia)` }, false)} title="Duplicar" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100" data-testid={`comparativa-duplicar-${c._id}`}><Copy size={14} /></button>
                    <button onClick={() => borrar(c)} title="Borrar" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50" data-testid={`comparativa-borrar-${c._id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {abierta && <Editor key={abierta._id} inicial={abierta} onClose={() => { setAbierta(null); cargar(); }} onSaved={cargar} />}
    </div>
  );
}
